import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  TextField, 
  Button, 
  Card, 
  CardContent, 
  Paper, 
  Divider, 
  useTheme, 
  Avatar,
  IconButton,
  Alert,
  Fade,
  useMediaQuery
} from '@mui/material';
import { 
  FaMapMarkerAlt, 
  FaPhone, 
  FaEnvelope, 
  FaFacebook, 
  FaTwitter, 
  FaInstagram, 
  FaLinkedin,
  FaQuestionCircle,
  FaCheckCircle,
  FaExclamationCircle
} from 'react-icons/fa';

/**
 * Contact page component with corporate design using Material UI
 */
const Contact = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState({
    submitting: false,
    submitted: false,
    error: null
  });

  // Contact information
  const contactInfo = [
    {
      id: 1,
      type: 'address',
      value: '123 Education Lane, Nairobi, Kenya 00100',
      icon: <FaMapMarkerAlt />
    },
    {
      id: 2,
      type: 'phone',
      value: '+254 75757575',
      icon: <FaPhone />
    },
    {
      id: 3,
      type: 'email',
      value: 'info@meira.co.ke',
      icon: <FaEnvelope />
    }
  ];

  // Social media links
  const socialLinks = [
    {
      id: 1,
      name: 'Facebook',
      url: 'https://facebook.com',
      icon: <FaFacebook />
    },
    {
      id: 2,
      name: 'Twitter',
      url: 'https://twitter.com',
      icon: <FaTwitter />
    },
    {
      id: 3,
      name: 'Instagram',
      url: 'https://instagram.com',
      icon: <FaInstagram />
    },
    {
      id: 4,
      name: 'LinkedIn',
      url: 'https://linkedin.com',
      icon: <FaLinkedin />
    }
  ];

  // FAQ items
  const faqItems = [
    {
      question: 'How do I get started with Meira?',
      answer: 'Simply register for an account on our platform, and follow the onboarding process. You\'ll have access to our basic features right away.'
    },
    {
      question: 'Is there a free trial available?',
      answer: 'Yes, we offer a free trial for all new users, giving you full access to explore our platform\'s capabilities.'
    },
    {
      question: 'How can schools integrate with Meira?',
      answer: 'We offer dedicated integration services for schools. Contact our support team, and we\'ll guide you through the process.'
    },
    {
      question: 'What support resources are available?',
      answer: 'We provide comprehensive documentation, video tutorials, webinars, and personal support via email and chat.'
    }
  ];

  // Form handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, submitted: false, error: null });

    try {
      // In a real application, you would send the form data to your backend
      // await api.post('/contact', formData);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setStatus({ submitting: false, submitted: true, error: null });
      setFormData({ name: '', email: '', subject: '', message: '' });
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setStatus(prev => ({ ...prev, submitted: false }));
      }, 5000);
    } catch (error) {
      setStatus({ submitting: false, submitted: false, error: 'Failed to send message. Please try again.' });
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Meira | Get in Touch</title>
        <meta name="description" content="Contact the Meira team for questions, support, or partnership opportunities. We're here to help!" />
      </Helmet>

      {/* Hero section */}
      <Box 
        sx={{
          bgcolor: theme.palette.primary.main,
          py: { xs: 8, md: 12 },
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, transparent 50%)`,
            opacity: 0.6,
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Fade in={true} timeout={1000}>
            <Box textAlign="center">
              <Typography 
                variant="h1" 
                component="h1" 
                sx={{ 
                  fontWeight: 800, 
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                  mb: 2
                }}
              >
                Get in Touch
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 400, 
                  maxWidth: '800px', 
                  mx: 'auto',
                  opacity: 0.9
                }}
              >
                We'd love to hear from you! Send us a message and we'll respond as soon as possible.
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Contact form section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Grid container spacing={8}>
            <Grid item xs={12} md={6}>
              <Fade in={true} timeout={1000} style={{ transitionDelay: '100ms' }}>
                <Box>
                  <Typography variant="h3" component="h2" fontWeight={700} gutterBottom>
                    Contact Us
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
                    Whether you have a question about our platform, need help with your account, or want to explore partnership opportunities, we're ready to answer your questions.
                  </Typography>
                  
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Message"
                          name="message"
                          multiline
                          rows={4}
                          value={formData.message}
                          onChange={handleChange}
                          required
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          fullWidth
                          disabled={status.submitting}
                          sx={{ 
                            py: 1.5, 
                            fontWeight: 600,
                            boxShadow: 2,
                            '&:hover': {
                              boxShadow: 4,
                              transform: 'translateY(-2px)',
                              transition: 'all 0.3s ease'
                            }
                          }}
                        >
                          {status.submitting ? 'Sending...' : 'Send Message'}
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        {status.submitted && (
                          <Alert 
                            severity="success" 
                            icon={<FaCheckCircle />}
                            sx={{ mt: 2 }}
                          >
                            Message sent successfully! We'll get back to you soon.
                          </Alert>
                        )}
                        {status.error && (
                          <Alert 
                            severity="error" 
                            icon={<FaExclamationCircle />}
                            sx={{ mt: 2 }}
                          >
                            {status.error}
                          </Alert>
                        )}
                      </Grid>
                    </Grid>
                  </form>
                </Box>
              </Fade>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Fade in={true} timeout={1000} style={{ transitionDelay: '200ms' }}>
                <Paper 
                  elevation={3} 
                  sx={{ 
                    height: '100%', 
                    borderRadius: 2,
                    overflow: 'hidden',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Box 
                    sx={{ 
                      p: 4,
                      bgcolor: theme.palette.primary.dark,
                      color: 'white'
                    }}
                  >
                    <Typography variant="h4" component="h3" fontWeight={600} gutterBottom>
                      Our Information
                    </Typography>
                    <Typography variant="body1">
                      Find us at any of the following channels
                    </Typography>
                  </Box>
                  <Box sx={{ p: 4, flexGrow: 1 }}>
                    {contactInfo.map((item) => (
                      <Box 
                        key={item.id} 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mb: 4
                        }}
                      >
                        <Avatar 
                          sx={{ 
                            bgcolor: theme.palette.primary.main,
                            width: 56,
                            height: 56,
                            mr: 3,
                            boxShadow: 2,
                            color: 'white'
                          }}
                        >
                          {item.icon}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" component="h4" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                            {item.type}
                          </Typography>
                          <Typography variant="body1" color="text.secondary">
                            {item.value}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                    
                    <Divider sx={{ my: 3 }} />
                    
                    <Typography variant="h6" component="h4" fontWeight={600} sx={{ mb: 2 }}>
                      Follow Us
                    </Typography>
                    <Box>
                      {socialLinks.map((social) => (
                        <IconButton
                          key={social.id}
                          aria-label={social.name}
                          component="a"
                          href={social.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ 
                            mr: 1, 
                            bgcolor: 'background.paper',
                            color: theme.palette.primary.main,
                            boxShadow: 1,
                            '&:hover': {
                              bgcolor: theme.palette.primary.main,
                              color: 'white',
                              transform: 'translateY(-2px)',
                              transition: 'all 0.3s ease',
                              boxShadow: 2
                            }
                          }}
                        >
                          {social.icon}
                        </IconButton>
                      ))}
                    </Box>
                  </Box>
                </Paper>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Map Section */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Fade in={true} timeout={1000}>
            <Box>
              <Typography variant="h4" component="h2" fontWeight={700} textAlign="center" gutterBottom>
                Visit Our Office
              </Typography>
              <Typography variant="body1" color="text.secondary" textAlign="center" paragraph sx={{ mb: 6, maxWidth: '800px', mx: 'auto' }}>
                Come visit us at our headquarters for an in-person meeting or discussion about how we can help your educational journey.
              </Typography>
              
              <Paper 
                elevation={3} 
                sx={{ 
                  borderRadius: 2,
                  overflow: 'hidden',
                  height: 400,
                  position: 'relative',
                  bgcolor: 'grey.200',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {/* Here you would integrate a map component */}
                <Typography variant="body1" color="text.secondary">
                  Interactive Map
                </Typography>
              </Paper>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* FAQ section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: theme.palette.grey[50] }}>
        <Container maxWidth="lg">
          <Fade in={true} timeout={1000}>
            <Box textAlign="center" mb={6}>
              <Typography 
                variant="overline" 
                component="p" 
                color="primary" 
                fontWeight={600} 
                sx={{ mb: 1, letterSpacing: 1.5 }}
              >
                FREQUENT QUESTIONS
              </Typography>
              <Typography variant="h3" component="h2" fontWeight={700} gutterBottom>
                Frequently Asked Questions
              </Typography>
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{ 
                  maxWidth: 700, 
                  mx: 'auto' 
                }}
              >
                Find quick answers to common questions about our platform and services.
              </Typography>
            </Box>
          </Fade>
          
          <Grid container spacing={4}>
            {faqItems.map((faq, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Fade in={true} timeout={1000} style={{ transitionDelay: `${200 + (index * 100)}ms` }}>
                  <Card 
                    elevation={2} 
                    sx={{ 
                      height: '100%',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      },
                      borderRadius: 2
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Box display="flex" alignItems="flex-start" mb={2}>
                        <Avatar 
                          sx={{ 
                            bgcolor: theme.palette.primary.main, 
                            width: 40, 
                            height: 40,
                            mr: 2
                          }}
                        >
                          <FaQuestionCircle />
                        </Avatar>
                        <Typography variant="h6" component="h3" fontWeight={600}>
                          {faq.question}
                        </Typography>
                      </Box>
                      <Typography variant="body1" color="text.secondary" sx={{ pl: 7 }}>
                        {faq.answer}
                      </Typography>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Call to action */}
      <Box 
        sx={{ 
          py: { xs: 8, md: 10 }, 
          bgcolor: theme.palette.primary.main,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: `linear-gradient(45deg, ${theme.palette.primary.dark} 0%, transparent 70%)`,
            opacity: 0.6,
          }
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative' }}>
          <Fade in={true} timeout={1000}>
            <Box textAlign="center">
              <Typography variant="h3" component="h2" fontWeight={700} gutterBottom>
                Ready to Transform Education?
              </Typography>
              <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
                Join Meira today and be part of the future of education in Africa
              </Typography>
              <Button 
                variant="contained" 
                color="secondary" 
                size="large"
                sx={{ 
                  py: 1.5, 
                  px: 4, 
                  fontSize: '1.1rem',
                  boxShadow: 4,
                  '&:hover': {
                    boxShadow: 8,
                    transform: 'translateY(-3px)',
                    transition: 'all 0.3s ease'
                  }
                }}
              >
                Get Started
              </Button>
            </Box>
          </Fade>
        </Container>
      </Box>
    </>
  );
};

export default Contact;
